import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Calculator, CheckCircle2, Link2, Linkedin, Loader2, MessageCircle, Share2, TrendingUp, Twitter } from "lucide-react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];
const CATEGORIES = ["social", "shopping", "streaming", "tech", "finance", "travel", "telecom", "gaming", "dating", "lifestyle"];

export const useAnimatedNumber = (target) => {
  const [display, setDisplay] = useState(target);
  const ref = useRef(target);
  useEffect(() => {
    const from = ref.current;
    if (from === target) return;
    const start = performance.now();
    const dur = 700;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else ref.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return display;
};

export default function Estimate() {
  const { t } = useTranslation();
  const [services, setServices] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [prefilled, setPrefilled] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/estimate/services`).then((r) => setServices(r.data.services)).catch(() => setServices([]));
    axios
      .get(`${API}/discover`, { withCredentials: true })
      .then((r) => {
        const ids = r.data.service_ids || [];
        if (ids.length > 0) {
          setSelected(new Set(ids));
          setPrefilled(true);
        }
      })
      .catch(() => {});
  }, []);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    setResult(null);
  };

  const liveTotal = (services || []).reduce((sum, s) => sum + (selected.has(s.id) ? s.value : 0), 0);
  const animatedLive = useAnimatedNumber(liveTotal);
  const animatedResult = useAnimatedNumber(result?.annual_value || 0);

  const calculate = async () => {
    if (selected.size === 0 || calculating) return;
    setCalculating(true);
    try {
      const { data } = await axios.post(`${API}/estimate`, { service_ids: [...selected] });
      setResult(data);
      setTimeout(() => document.getElementById("estimate-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    } catch (e) {
      toast.error(t("estimate.error"));
    } finally {
      setCalculating(false);
    }
  };

  useEffect(() => {
    const original = document.title;
    document.title = t("estimate.metaTitle");
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", t("estimate.metaDesc"));
    return () => { document.title = original; };
  }, [t]);

  const shareUrl = `${window.location.origin}/estimateur`;
  const shareText = result ? t("estimate.shareText", { value: result.annual_value }) : "";
  const shareFull = `${shareText} ${shareUrl}`;
  const shareLinks = result
    ? [
        { id: "x", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareFull)}` },
        { id: "linkedin", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}` },
        { id: "whatsapp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(shareFull)}` },
      ]
    : [];

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareFull);
      toast.success(t("estimate.shareCopied"));
    } catch (e) {
      toast.error(t("estimate.error"));
    }
  };

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: t("estimate.metaTitle"), text: shareText, url: shareUrl });
      } catch (e) {
        // user dismissed the share sheet
      }
    } else {
      copyShare();
    }
  };

  const maxCat = result ? Math.max(...result.by_category.map((c) => c.value), 1) : 1;

  return (
    <main data-testid="estimate-page">
      <Header />
      <div className="pt-32 pb-40 max-w-4xl mx-auto px-6 lg:px-10 min-h-screen">
        <Link to="/" data-testid="estimate-back-link" className="inline-flex items-center gap-2 text-sm text-white/50 hover:text-mint transition-colors duration-200">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {t("estimate.back")}
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-mint/10 border border-mint/20 flex items-center justify-center">
            <Calculator className="w-6 h-6 text-mint" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="font-display font-semibold tracking-tighter text-white text-3xl sm:text-4xl">{t("estimate.title")}</h1>
            <p className="mt-1 text-sm text-white/50">{t("estimate.subtitle")}</p>
          </div>
        </div>

        {prefilled && (
          <p data-testid="estimate-prefilled" className="mt-6 inline-flex items-center gap-2 rounded-full bg-mint/10 border border-mint/25 px-4 py-1.5 text-xs text-mint">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2} />
            {t("estimate.prefilled")}
          </p>
        )}

        {services === null ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-mint animate-spin" strokeWidth={1.5} /></div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }} className="mt-10">
            {CATEGORIES.map((cat) => {
              const items = services.filter((s) => s.category === cat);
              if (items.length === 0) return null;
              return (
                <div key={cat} className="mt-8">
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-3">{t(`discover.categories.${cat}`)}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {items.map((s) => (
                      <button
                        key={s.id}
                        onClick={() => toggle(s.id)}
                        data-testid={`estimate-service-${s.id}`}
                        className={`rounded-xl border px-4 py-3 text-sm text-left transition-colors duration-200 ${
                          selected.has(s.id) ? "border-mint/60 bg-mint/10 text-white" : "border-white/10 bg-card text-white/60 hover:border-white/25"
                        }`}
                      >
                        <span className="flex items-center justify-between gap-2">
                          {s.name}
                          {selected.has(s.id) && <CheckCircle2 className="w-4 h-4 text-mint shrink-0" strokeWidth={2} />}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        <AnimatePresence>
          {result && (
            <motion.div
              id="estimate-result"
              data-testid="estimate-result"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              transition={{ duration: 0.6, ease: EASE }}
              className="mt-14 rounded-3xl border border-mint/25 bg-gradient-to-br from-mint/10 via-card to-night p-8 lg:p-12 scroll-mt-28"
            >
              <p className="text-xs font-medium uppercase tracking-[0.25em] text-mint">{t("estimate.resultKicker")}</p>
              <p data-testid="estimate-result-value" className="mt-4 font-display font-semibold tracking-tighter text-white text-5xl sm:text-6xl">
                ≈ {animatedResult} €<span className="text-white/40 text-2xl sm:text-3xl"> / {t("estimate.year")}</span>
              </p>
              <p className="mt-2 text-sm text-white/55">{t("estimate.monthlyLine", { value: result.monthly_value })}</p>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40">{t("estimate.shareTitle")}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={shareNative}
                    data-testid="estimate-share-native"
                    className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint"
                    aria-label="Share"
                  >
                    <Share2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                  {shareLinks.map((l) => (
                    <a
                      key={l.id}
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`estimate-share-${l.id}`}
                      className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint"
                      aria-label={l.id}
                    >
                      <l.icon className="w-4 h-4" strokeWidth={1.5} />
                    </a>
                  ))}
                  <button
                    onClick={copyShare}
                    data-testid="estimate-share-copy"
                    className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white/70 transition-colors duration-200 hover:border-mint/50 hover:text-mint"
                    aria-label="Copy link"
                  >
                    <Link2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                </div>
              </div>

              <div className="mt-10 grid lg:grid-cols-2 gap-10">
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">{t("estimate.breakdownTitle")}</p>
                  <ul className="space-y-4">
                    {result.by_category.map((c, i) => (
                      <li key={c.category} data-testid={`estimate-category-${c.category}`}>
                        <div className="flex justify-between text-xs text-white/60 mb-1.5">
                          <span>{t(`discover.categories.${c.category}`)}</span>
                          <span className="text-white/90 font-medium">{c.value} €</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${(c.value / maxCat) * 100}%` }}
                            transition={{ duration: 1, delay: 0.2 + i * 0.12, ease: EASE }}
                            className="h-full rounded-full bg-mint/70"
                          />
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-[0.2em] text-white/40 mb-4">{t("estimate.topTitle")}</p>
                  <ul className="space-y-3">
                    {result.top_services.map((s) => (
                      <li key={s.id} data-testid={`estimate-top-${s.id}`} className="flex items-center justify-between rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3">
                        <span className="text-sm text-white/80">{s.name}</span>
                        <span className="text-sm font-semibold text-mint">{s.value} €</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <p className="mt-8 text-xs text-white/45 leading-relaxed">{t("estimate.averageNote")}</p>
              <p className="mt-2 text-[11px] text-white/30 leading-relaxed">{t("estimate.disclaimer")}</p>

              <div className="mt-8 rounded-2xl border border-mint/30 bg-mint/10 p-6 flex flex-wrap items-center justify-between gap-4">
                <div className="max-w-sm">
                  <p className="font-display font-semibold text-white">{t("estimate.ctaTitle")}</p>
                  <p className="mt-1 text-xs text-white/55 leading-relaxed">{t("estimate.ctaText")}</p>
                </div>
                <a
                  href="/#pricing"
                  data-testid="estimate-signup-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-mint px-6 py-3 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105"
                >
                  {t("estimate.ctaButton")}
                  <ArrowRight className="w-4 h-4" strokeWidth={2} />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selected.size > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="fixed bottom-6 inset-x-0 z-40 flex justify-center px-6 pointer-events-none"
          >
            <div className="pointer-events-auto flex flex-wrap items-center justify-center gap-x-5 gap-y-3 rounded-full border border-mint/30 bg-night/90 backdrop-blur-xl px-6 py-3.5 shadow-2xl shadow-black/50">
              <span className="flex items-center gap-2.5">
                <TrendingUp className="w-4 h-4 text-mint shrink-0" strokeWidth={2} />
                <span data-testid="estimate-counter" className="font-display font-semibold text-white text-lg whitespace-nowrap">≈ {animatedLive} € / {t("estimate.year")}</span>
                <span className="hidden sm:inline text-xs text-white/40 whitespace-nowrap">{t("estimate.liveCount", { count: selected.size })}</span>
              </span>
              <button
                onClick={calculate}
                disabled={calculating}
                data-testid="estimate-calc-button"
                className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-2.5 text-sm font-semibold text-night transition-transform duration-200 hover:scale-105 disabled:opacity-50"
              >
                {calculating ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Calculator className="w-4 h-4" strokeWidth={2} />}
                {calculating ? t("estimate.calculating") : t("estimate.calcCta")}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <Footer />
    </main>
  );
}
